import {
  ArchivingClient,
  DismissArchivingResultsDTO,
  PreviewFiltersDTO,
  ResponseDTO,
  ResponseWithValueDTOOfArchivingLocationDTO,
  ResponseWithValueDTOOfListOfArchivedTaskDTO,
  ResponseWithValueDTOOfListOfArchivingJobExperimentResultDTO,
  ResponseWithValueDTOOfListOfPreviewResultRowDTO,
  ResponseWithValueDTOOfListOfString,
  RunArchivingDTO,
  RunUnarchiveDTO,
} from 'business/models/Models';
import BaseService from './BaseService';
import ModelFactory from 'business/models/ModelFactory';
import { getPlainDto } from 'transformers/utils';

export default class ArchivingService extends BaseService {
  async fetchPreviewArchivingData(
    filters: PreviewFiltersDTO,
  ): Promise<ResponseWithValueDTOOfListOfPreviewResultRowDTO> {
    const archivingClient: ArchivingClient = ModelFactory(ArchivingClient, false, true);
    return getPlainDto(await archivingClient.preview(filters));
  }

  async startArchiving(
    request: RunArchivingDTO,
  ): Promise<ResponseWithValueDTOOfListOfArchivingJobExperimentResultDTO> {
    const archivingClient: ArchivingClient = ModelFactory(ArchivingClient, false, true);
    return getPlainDto(await archivingClient.run(request));
  }

  async startUnarchiving(
    request: RunUnarchiveDTO,
  ): Promise<ResponseWithValueDTOOfListOfArchivingJobExperimentResultDTO> {
    const archivingClient: ArchivingClient = ModelFactory(ArchivingClient, false, true);
    return getPlainDto(await archivingClient.unarchive(request));
  }

  async fetchActiveArchivingJobResults(
    jobId?: number | string,
  ): Promise<ResponseWithValueDTOOfListOfArchivingJobExperimentResultDTO> {
    const archivingClient: ArchivingClient = ModelFactory(ArchivingClient, false, true);
    const response = await archivingClient.getActiveJobResults(
      jobId != null ? Number(jobId) : null,
    );
    return getPlainDto(
      ResponseWithValueDTOOfListOfArchivingJobExperimentResultDTO.fromJS(response),
    );
  }

  async fetchUndismissedArchivingJobResults(): Promise<ResponseWithValueDTOOfListOfArchivingJobExperimentResultDTO> {
    const archivingClient: ArchivingClient = ModelFactory(ArchivingClient, false, true);
    const response = await archivingClient.getUndismissedResults();
    return getPlainDto(
      ResponseWithValueDTOOfListOfArchivingJobExperimentResultDTO.fromJS(response),
    );
  }

  async fetchUndismissedUnarchivingJobResults(): Promise<ResponseWithValueDTOOfListOfArchivingJobExperimentResultDTO> {
    const archivingClient: ArchivingClient = ModelFactory(ArchivingClient, false, true);
    const response = await archivingClient.getUndismissedUnarchiveResults();
    return getPlainDto(
      ResponseWithValueDTOOfListOfArchivingJobExperimentResultDTO.fromJS(response),
    );
  }

  async fetchArchivingJobNames(): Promise<ResponseWithValueDTOOfListOfString> {
    const archivingClient: ArchivingClient = ModelFactory(ArchivingClient, false, true);
    return getPlainDto(await archivingClient.getNames());
  }

  async fetchArchivingLocation(): Promise<ResponseWithValueDTOOfArchivingLocationDTO> {
    const archivingClient: ArchivingClient = ModelFactory(ArchivingClient, false, true);
    return getPlainDto(await archivingClient.getLocation());
  }

  async pauseArchiving(): Promise<ResponseDTO> {
    const archivingClient: ArchivingClient = ModelFactory(ArchivingClient, false, true);
    return getPlainDto(await archivingClient.pause());
  }

  async resumeArchiving(): Promise<ResponseDTO> {
    const archivingClient: ArchivingClient = ModelFactory(ArchivingClient, false, true);
    return getPlainDto(await archivingClient.resume());
  }

  async startOverArchiving(includeArchivedFiles = false): Promise<ResponseDTO> {
    const archivingClient: ArchivingClient = ModelFactory(ArchivingClient, false, true);
    return getPlainDto(await archivingClient.startOver(includeArchivedFiles));
  }

  async fetchArchivedTasks(): Promise<ResponseWithValueDTOOfListOfArchivedTaskDTO> {
    const archivingClient: ArchivingClient = ModelFactory(ArchivingClient, false, true);
    return getPlainDto(await archivingClient.getArchivedTasks());
  }

  async fetchArchivedExperiments(
    jobIds: number[],
  ): Promise<ResponseWithValueDTOOfListOfArchivingJobExperimentResultDTO> {
    const archivingClient: ArchivingClient = ModelFactory(ArchivingClient, false, true);
    return getPlainDto(await archivingClient.getArchivedExperiments(jobIds));
  }

  async dismissArchivingResults(experimentIds: number[]): Promise<ResponseDTO> {
    const archivingClient: ArchivingClient = ModelFactory(ArchivingClient, false, true);
    return getPlainDto(
      await archivingClient.dismissResults(
        new DismissArchivingResultsDTO({ experimentIds }),
      ),
    );
  }
}
